import type { WorkflowHistoryPeriod } from "./routes/admin/workflows/workflow-history-chart";

export type WorkflowHistoryUrlState = {
  period: WorkflowHistoryPeriod;
  workflow: string[];
  status: string[];
  from?: string;
  to?: string;
};

export const WORKFLOW_HISTORY_STATUS_VALUES = [
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled",
] as const;

export const WORKFLOW_HISTORY_PERIOD_VALUES = [
  "1h",
  "24h",
  "7d",
  "30d",
  "custom",
] as const satisfies readonly WorkflowHistoryPeriod[];

export const WORKFLOW_HISTORY_CUSTOM_RETENTION_DAYS = 30;

export type WorkflowHistoryDateBounds = {
  min: string;
  max: string;
};

const PRESET_PERIODS = new Set<Exclude<WorkflowHistoryPeriod, "custom">>(
  WORKFLOW_HISTORY_PERIOD_VALUES.filter((period): period is Exclude<WorkflowHistoryPeriod, "custom"> => period !== "custom"),
);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

function validDate(value: string | null): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function dateToUtcDay(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function workflowHistoryDateBounds(asOf = new Date()): WorkflowHistoryDateBounds {
  const max = new Date(Date.UTC(
    asOf.getUTCFullYear(),
    asOf.getUTCMonth(),
    asOf.getUTCDate(),
  ));
  const min = new Date(max.getTime() - WORKFLOW_HISTORY_CUSTOM_RETENTION_DAYS * DAY_MS);
  return { min: dateToUtcDay(min), max: dateToUtcDay(max) };
}

export function isWorkflowHistoryDateRange(
  from: string | undefined,
  to: string | undefined,
  asOf = new Date(),
): from is string {
  if (!from || !to || !validDate(from) || !validDate(to) || from > to) return false;
  const bounds = workflowHistoryDateBounds(asOf);
  return from >= bounds.min && to <= bounds.max;
}

export function readWorkflowHistoryUrlState(
  params: URLSearchParams,
  asOf = new Date(),
): WorkflowHistoryUrlState {
  return normaliseWorkflowHistoryUrlState({
    period: params.get("period"),
    workflow: params.getAll("workflow"),
    status: params.getAll("status"),
    from: params.get("from"),
    to: params.get("to"),
  }, asOf);
}

export type WorkflowHistoryQueryValues = {
  period?: string | null;
  workflow?: string[] | null;
  status?: string[] | null;
  from?: string | null;
  to?: string | null;
};

export function normaliseWorkflowHistoryUrlState(
  values: WorkflowHistoryQueryValues,
  asOf = new Date(),
): WorkflowHistoryUrlState {
  const rawFrom = values.from ?? null;
  const rawTo = values.to ?? null;
  const parsedFrom = validDate(rawFrom) ? rawFrom : undefined;
  const parsedTo = validDate(rawTo) ? rawTo : undefined;
  const bounds = workflowHistoryDateBounds(asOf);
  const from = parsedFrom && parsedFrom >= bounds.min && parsedFrom <= bounds.max
    ? parsedFrom
    : undefined;
  const to = parsedTo && parsedTo >= bounds.min && parsedTo <= bounds.max
    ? parsedTo
    : undefined;
  const reversed = Boolean(from && to && from > to);
  const requestedPeriod = values.period ?? null;
  const period = from && to && !reversed
    ? "custom"
    : requestedPeriod && PRESET_PERIODS.has(requestedPeriod as Exclude<WorkflowHistoryPeriod, "custom">)
      ? requestedPeriod as Exclude<WorkflowHistoryPeriod, "custom">
      : "24h";

  return {
    period,
    workflow: values.workflow ?? [],
    // Keep the exact URL values. Run statuses such as sleeping, timed_out and
    // rerun have their own lifecycle meaning and must reach the runs API raw.
    status: values.status ?? [],
    ...(!reversed && from ? { from } : {}),
    ...(!reversed && to ? { to } : {}),
  };
}

export function updateWorkflowHistoryUrl(
  current: URLSearchParams,
  update: Partial<WorkflowHistoryUrlState>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const setValues = (key: string, values: string[] | undefined) => {
    if (values === undefined) return;
    next.delete(key);
    values.forEach((value) => next.append(key, value));
  };

  setValues("workflow", update.workflow);
  setValues("status", update.status);

  if (update.period !== undefined) {
    if (update.period === "24h") next.delete("period");
    else next.set("period", update.period);
  }
  if (update.from !== undefined) {
    if (update.from) next.set("from", update.from);
    else next.delete("from");
  }
  if (update.to !== undefined) {
    if (update.to) next.set("to", update.to);
    else next.delete("to");
  }

  return next;
}
